import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { School, Users, GraduationCap, BookOpen, UserCheck, ArrowRight } from "lucide-react";

const initials = (n = "U") => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <AppShell role="admin"><p className="text-slate-500">Loading…</p></AppShell>;
  const s = data.stats;

  return (
    <AppShell role="admin">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
        Welcome, Admin <span aria-hidden>👋</span>
      </h1>
      <p className="mt-1 text-slate-500">Manage schools, teachers, students and more.</p>

      <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat icon={School}        color="bg-violet-100 text-violet-600"   label="Schools"  value={s.schools} />
        <Stat icon={GraduationCap} color="bg-indigo-100 text-indigo-600"   label="Teachers" value={s.teachers} />
        <Stat icon={Users}         color="bg-emerald-100 text-emerald-600" label="Students" value={s.students} />
        <Stat icon={UserCheck}     color="bg-sky-100 text-sky-600"         label="Parents"  value={s.parents} />
        <Stat icon={BookOpen}      color="bg-amber-100 text-amber-600"     label="Modules"  value={s.modules} />
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-3">
        <QuickCard to="/admin/schools"  title="Add a School"     desc="Onboard a new school with code, name & address." />
        <QuickCard to="/admin/import"   title="Bulk Import Students" desc="Upload a CSV to onboard a class in seconds." />
        <QuickCard to="/admin/modules"  title="Manage Learning Modules" desc="Add, edit or delete modules across all portals." />
        <QuickCard to="/admin/backup"   title="Backup & Restore" desc="Export or restore the full database (sync Preview ↔ Production)." />
      </div>

      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <h3 className="text-lg font-bold text-slate-900">Recent Students</h3>
        <p className="text-sm text-slate-500">Most recently onboarded students.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">Student</th>
                <th className="py-3 pr-4">Username</th>
                <th className="py-3 pr-4">School</th>
                <th className="py-3 pr-4">Class</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_students.map((st) => (
                <tr key={st.id} className="border-b border-slate-50">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">{initials(st.full_name)}</div>
                      <span className="font-medium text-slate-900">{st.full_name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-700">{st.username}</td>
                  <td className="py-3 pr-4 text-slate-600">{st.school_name}</td>
                  <td className="py-3 pr-4 text-slate-600">{st.class_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

const Stat = ({ icon: Icon, color, label, value }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
    <div className="flex items-start gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}><Icon size={20} /></div>
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  </div>
);

const QuickCard = ({ to, title, desc }) => (
  <Link to={to} className="group rounded-2xl border border-slate-100 bg-white p-6 shadow-card transition-all hover:border-indigo-200 hover:shadow-card-hover">
    <h4 className="text-base font-bold text-slate-900">{title}</h4>
    <p className="mt-1 text-sm text-slate-500">{desc}</p>
    <p className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 group-hover:gap-2 group-hover:text-indigo-700 transition-all">
      Open <ArrowRight size={14} />
    </p>
  </Link>
);
